import { useEffect, useMemo, useState } from 'react';
import { isAxiosError } from 'axios';
import { stringify as stringifyYaml } from 'yaml';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  RECEIPT_TYPE_LABEL,
  RECEIPT_TYPE_LABEL_KEY,
  SCOPE_LABEL,
  SCOPE_LABEL_KEY,
  useReceiptConfigCurrent,
  usePutReceiptConfig,
  type ReceiptConfigScope,
  type ReceiptType,
} from '@/hooks/use-receipt-config';
import { useT } from '@/i18n/use-t';
import { MonacoYamlEditor } from './monaco-yaml-editor';
import { parseReceiptConfigYaml } from './receipt-config-yaml';

const SCOPES: ReceiptConfigScope[] = ['default', 'customer', 'location', 'terminal-profile'];

const RECEIPT_TYPES: ReceiptType[] = [
  'sale',
  'refund',
  'void',
  'parked',
  'x_report',
  'z_report',
  'copy',
];

function extractError(err: unknown): string | null {
  if (isAxiosError(err)) {
    const body = err.response?.data as { message?: string; code?: string } | undefined;
    if (body?.message) return body.code ? `${body.code}: ${body.message}` : body.message;
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return null;
}

/**
 * Receipt Config editor (Sprint 10 addendum to receipt-config spec).
 *
 * Two-panel layout: scope/type picker on the left, lazy-loaded
 * Monaco YAML editor on the right. Authors edit YAML; on save the
 * editor parses to JSON and PUTs to /receipt-configs. Validation
 * is YAML-syntax-only in this revision — schema-driven (zod-to-
 * json-schema) inline diagnostics ride the next sprint per the
 * addendum's "Out of scope: schema validator (Sprint 11)" carve.
 * IR-piped preview also deferred (no server-side preview endpoint
 * exists yet).
 */
export function ReceiptConfigEditorPage() {
  const t = useT('admin_ui');
  const [scopeType, setScopeType] = useState<ReceiptConfigScope>('default');
  const [scopeId, setScopeId] = useState('');
  const [receiptType, setReceiptType] = useState<ReceiptType>('sale');
  const [yamlText, setYamlText] = useState('# Empty config — start typing.\n');
  const [banner, setBanner] = useState<string | null>(null);
  const [saveOk, setSaveOk] = useState<string | null>(null);

  const current = useReceiptConfigCurrent(
    { scopeType, scopeId: scopeType === 'default' ? null : scopeId || null, receiptType },
    scopeType === 'default' || scopeId.length > 0,
  );
  const save = usePutReceiptConfig();

  // Sync editor when the row reloads.
  useEffect(() => {
    if (!current.data) {
      if (current.isFetched) {
        setYamlText('# No config yet for this (scope, type). Type your YAML below and save.\n');
      }
      return;
    }
    setYamlText(stringifyYaml(current.data.jsonConfig ?? {}));
    setBanner(null);
    setSaveOk(null);
  }, [current.data?.id, current.data?.version, current.isFetched]);

  // A blank or comment-only buffer is "nothing yet": no error banner, and
  // Save stays disabled until there is a YAML object to persist (RL-366).
  const parsed = useMemo(() => parseReceiptConfigYaml(yamlText), [yamlText]);

  async function onSave() {
    setBanner(null);
    setSaveOk(null);
    if (parsed.kind === 'empty') return;
    if (parsed.kind === 'error') {
      setBanner(`${t('receiptConfig.yamlParseError', 'YAML parse error:')} ${parsed.error}`);
      return;
    }
    try {
      const result = await save.mutateAsync({
        scopeType,
        scopeId: scopeType === 'default' ? null : scopeId || null,
        receiptType,
        jsonConfig: parsed.value,
      });
      setSaveOk(
        t('receiptConfig.saved', 'Saved v{version} at {time}')
          .replace('{version}', String(result.version))
          .replace('{time}', new Date(result.createdAt).toLocaleString()),
      );
    } catch (err) {
      setBanner(extractError(err) ?? t('receiptConfig.saveFailed', 'Save failed.'));
    }
  }

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-semibold">
          {t('receiptConfig.title', 'Receipt config editor')}
        </h1>
        {/* The editor parses YAML to JSON for persistence; schema diagnostics and
            the rendered preview are not wired yet. Neither is operator copy (RL-331). */}
        <p className="text-sm text-muted-foreground">
          {t(
            'receiptConfig.intro',
            'Receipt layout settings for the selected scope. Write the layout in YAML below and save it to apply.',
          )}
        </p>
      </header>

      <div className="grid grid-cols-[280px_1fr] gap-5">
        <aside className="space-y-4 rounded-md border border-border bg-card p-4">
          <div>
            <Label htmlFor="scopeType">{t('receiptConfig.scope', 'Scope')}</Label>
            <select
              id="scopeType"
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={scopeType}
              onChange={(e) => setScopeType(e.target.value as ReceiptConfigScope)}
            >
              {SCOPES.map((s) => (
                <option key={s} value={s}>
                  {t(SCOPE_LABEL_KEY[s], SCOPE_LABEL[s])}
                </option>
              ))}
            </select>
          </div>
          {scopeType !== 'default' && (
            <div>
              <Label htmlFor="scopeId">
                {t('receiptConfig.scopeId', 'Scope id ({scope} id)').replace(
                  '{scope}',
                  t(SCOPE_LABEL_KEY[scopeType], SCOPE_LABEL[scopeType]),
                )}
              </Label>
              <input
                id="scopeId"
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                value={scopeId}
                onChange={(e) => setScopeId(e.target.value)}
                placeholder={t('receiptConfig.scopeIdPlaceholder', 'e.g. cmoq8r2...')}
              />
            </div>
          )}
          <div>
            <Label htmlFor="receiptType">{t('receiptConfig.receiptType', 'Receipt type')}</Label>
            <select
              id="receiptType"
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={receiptType}
              onChange={(e) => setReceiptType(e.target.value as ReceiptType)}
            >
              {RECEIPT_TYPES.map((rt) => (
                <option key={rt} value={rt}>
                  {t(RECEIPT_TYPE_LABEL_KEY[rt], RECEIPT_TYPE_LABEL[rt])}
                </option>
              ))}
            </select>
          </div>

          <div className="border-t border-border pt-3 text-xs text-muted-foreground">
            {current.isLoading ? (
              <p>{t('receiptConfig.loadingCurrentRow', 'Loading current row…')}</p>
            ) : current.data ? (
              <p>
                {t('receiptConfig.currentVersion', 'Current version:')}{' '}
                <code className="font-mono">v{current.data.version}</code>
                <br />
                {t('receiptConfig.updated', 'Updated:')}{' '}
                {new Date(current.data.createdAt).toLocaleString()}
              </p>
            ) : (
              <p>
                {t('receiptConfig.noConfigYet', 'No config persisted yet for this (scope, type).')}
              </p>
            )}
          </div>
        </aside>

        <main className="space-y-3">
          <MonacoYamlEditor value={yamlText} onChange={setYamlText} height={520} />
          {parsed.kind === 'error' && (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {t('receiptConfig.yamlParseError', 'YAML parse error:')} {parsed.error}
            </p>
          )}
          {banner && (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {banner}
            </p>
          )}
          {saveOk && (
            <p className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-700 dark:text-emerald-300">
              {saveOk}
            </p>
          )}
          <div className="flex gap-2">
            <Button onClick={onSave} disabled={parsed.kind !== 'ok' || save.isPending}>
              {save.isPending
                ? t('common.saving', 'Saving…')
                : t('receiptConfig.saveNewVersion', 'Save new version')}
            </Button>
          </div>
        </main>
      </div>
    </div>
  );
}
